enum Direction {
  LEFT = -1,
  RIGHT = 1,
}

class Lightbox {
  private readonly $dialog: HTMLDialogElement;
  private readonly $content: HTMLElement;

  private readonly $prev: HTMLElement | null;
  private readonly $next: HTMLElement | null;

  private readonly $images: NodeListOf<HTMLElement>;

  private isAnimating: boolean = false;

  constructor($root: HTMLElement) {
    const $dialog = document.querySelector<HTMLDialogElement>("#lightbox");
    if (!$dialog) {
      throw new Error("Lightbox needs a #lightbox element in the document");
    }

    const $content =
      $dialog.querySelector<HTMLImageElement>(".lightbox__content");
    if (!$content) {
      throw new Error(
        "Lightbox needs a .lightbox__image element inside the #lightbox",
      );
    }

    this.$dialog = $dialog;
    this.$content = $content;

    this.$prev = $dialog.querySelector(".lightbox__prev");
    this.$next = $dialog.querySelector(".lightbox__next");

    this.$images = $root.querySelectorAll<HTMLImageElement>("img");

    this.init();
  }

  private init() {
    this.$images.forEach(($img, index) => {
      $img.addEventListener("click", () => {
        this.open(index);
      });
    });

    this.$dialog.addEventListener("click", (evt) => {
      this.close();
    });

    this.$dialog.addEventListener("keydown", (evt) => {
      if (evt.key === "ArrowLeft") {
        this.move(Direction.LEFT);
      }
      if (evt.key === "ArrowRight") {
        this.move(Direction.RIGHT);
      }
    });

    this.$content.addEventListener("transitionstart", (evt) => {
      this.isAnimating = true;
    });

    this.$content.addEventListener("transitionend", (evt) => {
      this.isAnimating = false;
    });

    this.$prev?.addEventListener("click", (evt) => {
      evt.stopPropagation();
      this.move(Direction.LEFT);
    });

    this.$next?.addEventListener("click", (evt) => {
      evt.stopPropagation();
      this.move(Direction.RIGHT);
    });
  }

  private open(index: number) {
    this.$dialog.setAttribute(
      "data-image-count",
      this.$images.length.toString(),
    );
    this.$content.innerHTML = "";

    this.isAnimating = false;

    this.$dialog.showModal();

    index = this.normalizeIndex(index);

    const $img = this.$images[index].cloneNode(true) as HTMLImageElement;
    $img.classList.add("lightbox__image");

    this.$content.appendChild($img);

    this.$dialog.setAttribute("data-index", index.toString());
  }

  private close() {
    this.$dialog.close();
  }

  private move(dir: Direction) {
    if (
      this.isAnimating ||
      this.$content.classList.contains("slide-right") ||
      this.$content.classList.contains("slide-left") ||
      this.$images.length <= 1
    ) {
      return;
    }

    this.$content
      .querySelectorAll<HTMLElement>(
        ".lightbox__image--prev, .lightbox__image--next",
      )
      .forEach(($img) => {
        $img.remove();
      });

    const currIndex = parseInt(
      this.$dialog.getAttribute("data-index") || "0",
      10,
    );
    const nextIndex = this.normalizeIndex(currIndex + dir);

    this.$content
      .querySelector<HTMLElement>(".lightbox__image")
      ?.classList.add(
        dir === Direction.LEFT
          ? "lightbox__image--prev"
          : "lightbox__image--next",
      );

    const $img = this.$images[nextIndex].cloneNode(true) as HTMLImageElement;
    $img.classList.add("lightbox__image");
    this.$content.appendChild($img);

    this.$dialog.setAttribute("data-index", nextIndex.toString());

    const slideClass = dir === Direction.LEFT ? "slide-left" : "slide-right";

    this.$content.classList.add(slideClass);

    setTimeout(() => {
      this.$content.classList.remove(slideClass);
    }, 0);
  }

  private normalizeIndex(index: number) {
    return (
      ((index % this.$images.length) + this.$images.length) %
      this.$images.length
    );
  }
}

function init(scope: ParentNode = document) {
  scope
    .querySelectorAll<HTMLElement>(".lightbox-gallery")
    .forEach((el) => new Lightbox(el));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init());
} else {
  init();
}

export {};
